import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { connect } from 'react-redux';
import {
  setIsOrderNotificationsModalOpen,
  setIsSignUpNoticeModalOpen,
  setIsCheckoutModalOpen,
  setIsOrderInfoModalOpen,
  setOrderInfoModalData,
} from '../../../../redux/modal/modal.actions';
import {
  setCheckout as setCheckoutAction,
  setCart,
  setOrder,
  setOrderHash,
} from '../../../../redux/store/store.actions';
import { fetchMeWithPromise } from '../../../../redux/me/me.actions';
import { config } from '../../../../configs/app.config';
import {
  basePatchQuery,
  updateOrderV2,
  getCustomerProfile,
} from '../../../../utility/sb-api/sb-api.service';
import {
  generateWhatsAppLink,
  getOrderPathType,
} from '../../../../utility/util/util.service';
import TelFormField from '../../form/fields/TelFormField';
import { AvForm } from 'availity-reactstrap-validation';
import { MessageCircle, Smartphone, Hash, Mail } from 'react-feather';
import QRCode from 'react-qr-code';

// WhatsApp SVG icon (official logo shape)
const WhatsAppIcon = ({ size = 22, color = '#fff' }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill={color}>
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

const METHOD_META = {
  whatsapp: { Icon: WhatsAppIcon, label: 'WhatsApp', color: '#25D366' },
  sms: { Icon: Smartphone, label: 'SMS', color: '#2196F3' },
  pager: { Icon: Hash, label: 'Pager', color: '#FF9800' },
  email: { Icon: Mail, label: 'Email', color: '#E91E63' },
};

const TYPE_TO_KEY = {
  WhatsAppNotificationMethodConfig: 'whatsapp',
  SmsNotificationMethodConfig: 'sms',
  PagerNotificationMethodConfig: 'pager',
  EmailNotificationMethodConfig: 'email',
};

// localStorage key under which the checkout session is saved when the customer
// opens WhatsApp, so the "finish your order" button in the WhatsApp welcome
// message can restore the exact checkout state.
export const WHATSAPP_CHECKOUT_SESSION_KEY = 'whatsappCheckoutSession';

const OrderNotificationsModal = ({
  isOpen,
  store,
  data,
  me,
  currentOrder,
  cart,
  checkout,
  menuItems,
  menuItemDatas,
  kiosk,
  orderNotificationsModalData,
  noSignupModal,
  setIsSignUpNoticeModalOpen,
  setIsOrderNotificationsModalOpen,
  setIsCheckoutModalOpen,
  setCheckoutAction,
  fetchMeWithPromise,
  setOrderInfoModalData,
  setIsOrderInfoModalOpen,
  setCart,
  setOrder,
  setOrderHash,
}) => {
  const [customerProfile, setCustomerProfile] = useState(null);
  const [activeMethod, setActiveMethod] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingProfile, setIsLoadingProfile] = useState(false);
  const [pagerNumber, setPagerNumber] = useState('');
  const [smsPhone, setSmsPhone] = useState('');
  const [emailAddress, setEmailAddress] = useState('');

  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email || '');

  const intl = useIntl();
  // The order may be passed directly on the notifications data (order_only
  // post-order flow) or, in the legacy path, taken from the open order-info
  // modal's data.
  const order = orderNotificationsModalData?.order || data?.order;
  const checkoutFromData = orderNotificationsModalData?.checkout;
  const proceedToCheckout = orderNotificationsModalData?.proceedToCheckout;
  // order_only "checkout": this modal stands in for the checkout step of the
  // order_and_pay flow. The order exists but has NOT been submitted yet — it is
  // only finalized (submitted) once the customer confirms a notification
  // method, so dismissing the modal leaves the order unplaced and lets them go
  // back to add more items.
  const orderOnlyCheckout = orderNotificationsModalData?.orderOnlyCheckout;
  const primaryColor = store?.primaryColor || '#22442c';

  // Finalize an order_only order: submit it, then show the confirmation and
  // clear the cart/checkout state. Called after the notification method has
  // been registered (in handleConfirm) or when there is nothing to choose.
  const finalizeOrderOnly = () => {
    if (!orderOnlyCheckout || !order) return;
    const pathType = getOrderPathType(order.type);
    // Re-send the applied discount code with the submit so the discount is
    // deterministic and survives the status change regardless of which
    // relations the backend loads on update (the notification-method PATCH
    // above omits the code and would otherwise drop the discount).
    const appliedDiscountCode = order?.discountCode?.code;
    const submitPromise = pathType
      ? updateOrderV2(pathType, order.id, {
          status: 'submitted',
          ...(appliedDiscountCode && { discountCode: appliedDiscountCode }),
        }).catch(() => order)
      : Promise.resolve(order);
    submitPromise.then((submitted) => {
      const finalOrder = submitted || order;
      setCheckoutAction(null);
      setOrder(null);
      setOrderHash(null);
      setCart(null);
      setOrderInfoModalData({ order: finalOrder, isInitial: true });
      setIsOrderInfoModalOpen(true);
    });
  };

  // Dismiss the order_only prompt without placing the order. The un-submitted
  // draft is discarded so re-proceeding (after adding items) creates a fresh
  // order with the updated cart. The cart itself is kept.
  const dismissOrderOnly = () => {
    if (!orderOnlyCheckout) return;
    setOrder(null);
    setOrderHash(null);
  };

  // Order this modal is acting on: during checkout it is the order being placed
  // (the store copy, state.store.order); in the post-order prompt it is the
  // modal's order (data.order from orderInfoModalData). We look for the
  // notificationRecipient on both the modal order and the store copy — socket
  // 'order' events keep the store copy in sync — but only for the matching
  // order id, so a stale orderInfoModalData.order can't mask a fresh recipient
  // (nor a stale store order block a real one).
  const targetOrderId = proceedToCheckout
    ? currentOrder?.id
    : order?.id || currentOrder?.id;
  const alreadyHasNotificationMethod = !!(
    (order?.id === targetOrderId && order?.notificationRecipient) ||
    (currentOrder?.id === targetOrderId && currentOrder?.notificationRecipient)
  );

  // Resolve config
  const orderType = order?.type || checkoutFromData?.orderType;
  const orderConfigKey = {
    SelfServiceOrder: 'selfServiceOrderConfig',
    PickupOrder: 'pickupOrderConfig',
    InhouseOrder: 'inhouseOrderConfig',
    DeliveryOrder: 'deliveryOrderConfig',
    CateringOrder: 'cateringOrderConfig',
  }[orderType];
  const rawConfig = orderConfigKey ? store?.[orderConfigKey] : null;
  const currentOrderConfig = typeof rawConfig === 'string' ? null : rawConfig;
  // Get category IDs of items currently in cart
  // Cart items have embedded itemData with item reference (string ID)
  // Then look up the Item in menuItems to get its categories
  const cartCategoryIds = (cart || []).reduce((ids, cartItem) => {
    // Get item ID from cart item's embedded itemData or via menuItemDatas lookup
    let itemId = cartItem?.itemData?.item?.id || cartItem?.itemData?.item;
    if (!itemId) {
      const itemDataId = cartItem?.itemData?.id || cartItem?.id;
      const itemData = (menuItemDatas || []).find((d) => d.id === itemDataId);
      itemId = itemData?.item?.id || itemData?.item;
    }
    if (!itemId) return ids;
    // Find the Item to get its categories
    const menuItem = (menuItems || []).find((item) => item?.id === itemId);
    if (menuItem?.categories) {
      menuItem.categories.forEach((catId) => {
        if (typeof catId === 'string') ids.add(catId);
        else if (catId?.id) ids.add(catId.id);
      });
    }
    return ids;
  }, new Set());

  // Filter: enabled + validForCategories matches cart (or null/empty = all)
  // Sort by sortOrder for consistent ordering
  const enabledConfigs = (currentOrderConfig?.notificationMethodConfigs || [])
    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
    .filter((c) => {
    if (!c.isEnabled) return false;
    if (!c.validForCategories || c.validForCategories.length === 0) return true;
    // Check if any cart item matches the config's categories
    return c.validForCategories.some((cat) => cartCategoryIds.has(cat.id));
  });
  const pagerConfig = enabledConfigs.find((c) => c.type === 'PagerNotificationMethodConfig');

  const availableMethods = enabledConfigs
    .map((c) => TYPE_TO_KEY[c.type])
    .filter(Boolean);
  const hasAnyMethod = availableMethods.length > 0;

  // When a store has enabled notification methods, choosing one is mandatory
  // during order flows (checkout and the post-order registration prompt): the
  // customer must pick a method and actually provide it, and cannot skip.
  // Only the user-settings management view opts out via isClosable.
  const isMandatory = hasAnyMethod && !orderNotificationsModalData?.isClosable;

  // Re-select first available method when available methods change (e.g., after data loads)
  useEffect(() => {
    if (isOpen && availableMethods.length > 0 && !availableMethods.includes(activeMethod)) {
      setActiveMethod(availableMethods[0]);
    }
  }, [isOpen, availableMethods.join(',')]);

  useEffect(() => {
    if (isOpen && hasAnyMethod) {
      setPagerNumber('');
      setSmsPhone('');
      setEmailAddress(me?.email || me?.requester?.email || '');
      // First method (sorted by sortOrder) is pre-selected
      setActiveMethod(availableMethods[0] || null);

      if (!proceedToCheckout) {
        setIsLoadingProfile(true);
        fetchMeWithPromise()
          .then((freshMe) => {
            const profileId = freshMe?.customerProfileId || freshMe?.requester?.customerProfile?.id;
            setEmailAddress(
              (prev) => prev || freshMe?.email || freshMe?.requester?.email || '',
            );
            if (profileId) {
              return getCustomerProfile(profileId).then((profile) => {
                setCustomerProfile(profile);
                setSmsPhone(profile?.smsPhoneNumber || profile?.tel || '');
                setEmailAddress((prev) => prev || profile?.email || '');
              });
            }
          })
          .catch(() => {})
          .finally(() => setIsLoadingProfile(false));
      }
    }
  }, [isOpen]);

  // If no methods available (all filtered out by categories), auto-proceed
  useEffect(() => {
    if (isOpen && !hasAnyMethod && proceedToCheckout) {
      setIsOrderNotificationsModalOpen(false);
      setIsCheckoutModalOpen(true);
    } else if (isOpen && !hasAnyMethod && orderOnlyCheckout) {
      setIsOrderNotificationsModalOpen(false);
      finalizeOrderOnly();
    }
  }, [isOpen, hasAnyMethod, proceedToCheckout, orderOnlyCheckout]);

  // A notification method is already registered for this order (e.g. the
  // customer added one via WhatsApp and we received it over sockets). In the
  // legacy post-order prompt there's nothing left to do, so just close.
  // For the order_only "checkout" we deliberately do NOT auto-submit here:
  // the customer must still press Continue to confirm and place the order.
  useEffect(() => {
    if (
      isOpen &&
      !proceedToCheckout &&
      !orderOnlyCheckout &&
      alreadyHasNotificationMethod
    ) {
      setIsOrderNotificationsModalOpen(false);
    }
  }, [isOpen, proceedToCheckout, orderOnlyCheckout, alreadyHasNotificationMethod]);

  if (!hasAnyMethod) return null;
  // In the order_only flow, keep the modal visible after the method registers
  // so the customer can press Continue to confirm; only the legacy post-order
  // prompt hides itself once a method already exists.
  if (
    !proceedToCheckout &&
    !orderOnlyCheckout &&
    alreadyHasNotificationMethod
  )
    return null;

  // Persist the current checkout session before leaving for WhatsApp so the
  // "finish your order" button in the welcome message can restore this exact
  // state (order/cart/checkout) when the customer returns to the browser.
  const handleOpenWhatsApp = () => {
    try {
      localStorage.setItem(
        WHATSAPP_CHECKOUT_SESSION_KEY,
        JSON.stringify({
          orderId: currentOrder?.id || order?.id,
          checkout,
          cart,
        }),
      );
    } catch (err) {
      console.warn('Failed to persist WhatsApp checkout session:', err);
    }
  };

  const handleConfirm = () => {
    if (!activeMethod) return;
    if (proceedToCheckout) {
      const recipientMap = { pager: pagerNumber, sms: smsPhone, whatsapp: '', email: emailAddress };
      setCheckoutAction({
        notificationMethod: activeMethod,
        notificationRecipient: recipientMap[activeMethod] || '',
      });
      setIsOrderNotificationsModalOpen(false);
      setIsCheckoutModalOpen(true);
    } else if (order?.id) {
      setIsSubmitting(true);
      const done = () => {
        setIsSubmitting(false);
        setIsOrderNotificationsModalOpen(false);
        finalizeOrderOnly();
      };
      if (activeMethod === 'pager') {
        updateOrderV2(getOrderPathType(order.type), order.id, {
          notificationMethod: 'pager',
          notificationRecipient: pagerNumber,
        })
          .then(done)
          .catch(() => setIsSubmitting(false));
      } else if (activeMethod === 'sms' && customerProfile?.id) {
        basePatchQuery(`user/customer-profiles/${customerProfile.id}`, { smsPhoneNumber: smsPhone })
          .then(done)
          .catch(() => setIsSubmitting(false));
      } else if (activeMethod === 'email') {
        updateOrderV2(getOrderPathType(order.type), order.id, {
          notificationMethod: 'email',
          notificationRecipient: emailAddress,
        })
          .then(done)
          .catch(() => setIsSubmitting(false));
      } else {
        done();
      }
    }
  };

  const handleSkip = () => {
    setIsOrderNotificationsModalOpen(false);
    if (proceedToCheckout) {
      setIsCheckoutModalOpen(true);
    } else if (me?.isGuest && !noSignupModal) {
      setTimeout(() => setIsSignUpNoticeModalOpen(true), 1000);
    }
  };

  // Dismiss the modal without proceeding. Used both in the order_and_pay
  // checkout flow (go back to add items; reaching checkout still requires
  // completing this modal via Continue) and in the order_only flow, where the
  // un-submitted order is discarded so the customer can add items and place a
  // fresh order.
  const handleClose = () => {
    setIsOrderNotificationsModalOpen(false);
    dismissOrderOnly();
  };

  const isConfirmDisabled = () => {
    if (!activeMethod) return true;
    if (activeMethod === 'pager') {
      if (!pagerNumber) return true;
      const num = parseInt(pagerNumber);
      if (pagerConfig?.minNumber != null && num < pagerConfig.minNumber) return true;
      if (pagerConfig?.maxNumber != null && num > pagerConfig.maxNumber) return true;
    }
    if (activeMethod === 'sms' && !smsPhone) return true;
    if (activeMethod === 'email' && !isValidEmail(emailAddress)) return true;
    if (activeMethod === 'whatsapp') {
      // WhatsApp is only "added" once the customer taps the link and the
      // backend confirms the subscription over sockets (notificationRecipient
      // is set). Require that before continuing, whenever the link is actually
      // available. When no order exists yet, registration is deferred to the
      // post-order prompt, so allow continuing.
      const whatsappLinkAvailable = !!(currentOrder?.id || order?.id);
      if (whatsappLinkAvailable && !alreadyHasNotificationMethod) return true;
    }
    return false;
  };

  // Once a pager number has been entered, reflect its validity on the pager
  // input: 'invalid' when it is out of the configured min/max range, 'valid'
  // when it satisfies the range. Returns null for every other case so the
  // field keeps its default (primary) border.
  const getPagerValidity = () => {
    if (activeMethod !== 'pager' || pagerNumber.trim() === '') return null;
    const num = parseInt(pagerNumber);
    const invalid =
      Number.isNaN(num) ||
      (pagerConfig?.minNumber != null && num < pagerConfig.minNumber) ||
      (pagerConfig?.maxNumber != null && num > pagerConfig.maxNumber);
    return invalid ? 'invalid' : 'valid';
  };

  const pagerValidity = getPagerValidity();
  const pagerBorderColor =
    pagerValidity === 'invalid'
      ? '#dc3545'
      : pagerValidity === 'valid'
      ? '#28a745'
      : primaryColor;

  return (
    <Modal isOpen={isOpen} centered>
      <ModalHeader
        toggle={
          !isMandatory
            ? handleSkip
            : proceedToCheckout || orderOnlyCheckout
              ? handleClose
              : undefined
        }
        className="border-0 pb-0 px-4 pt-4"
      >
        <span style={{ fontSize: '1.2rem', fontWeight: 700, fontFamily: 'Lexend, sans-serif' }}>
          <FormattedMessage id="orderNotificationModalTitle" defaultMessage="Stay updated" />
        </span>
      </ModalHeader>

      <ModalBody className="px-4 pt-2 pb-3">
        <p style={{ color: '#999', fontSize: '0.85rem', marginBottom: '20px', fontFamily: 'Lexend, sans-serif' }}>
          <FormattedMessage
            id="orderNotificationModalChooseMethod"
            defaultMessage="How would you like to be notified?"
          />
        </p>

        {/* Method selector */}
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
          {availableMethods.map((key) => {
            const meta = METHOD_META[key];
            const IconComp = meta.Icon;
            const isActive = activeMethod === key;
            return (
              <div
                key={key}
                onClick={() => setActiveMethod(key)}
                style={{
                  flex: availableMethods.length <= 3 ? '1 1 0' : '0 0 auto',
                  minWidth: '90px',
                  maxWidth: '130px',
                  padding: '20px 12px 16px',
                  borderRadius: '14px',
                  border: `2px solid ${isActive ? primaryColor : '#eee'}`,
                  background: isActive ? `${primaryColor}0a` : '#fafafa',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                  textAlign: 'center',
                  fontFamily: 'Lexend, sans-serif',
                }}
              >
                <div style={{
                  width: '44px',
                  height: '44px',
                  borderRadius: '12px',
                  background: isActive ? meta.color : '#ddd',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 10px',
                  transition: 'background 0.15s ease',
                }}>
                  <IconComp size={22} color="#fff" />
                </div>
                <div style={{ fontSize: '0.8rem', fontWeight: 600, color: isActive ? '#222' : '#888', lineHeight: 1.2, transition: 'color 0.15s ease' }}>
                  {meta.label}
                </div>
              </div>
            );
          })}
        </div>

        {/* Input for selected method */}
        {activeMethod === 'pager' && (
          <div className="text-center mt-4">
            <input
              type="number"
              className={`form-control mx-auto text-center${
                pagerValidity === 'invalid'
                  ? ' pager-input-invalid'
                  : pagerValidity === 'valid'
                  ? ' pager-input-valid'
                  : ''
              }`}
              style={{
                maxWidth: '160px',
                fontSize: '1.75rem',
                fontWeight: 700,
                borderRadius: '12px',
                border: `2px solid ${pagerBorderColor}`,
                padding: '10px',
                letterSpacing: '2px',
              }}
              value={pagerNumber}
              onChange={(e) => setPagerNumber(e.target.value)}
              placeholder="#"
              min={pagerConfig?.minNumber || undefined}
              max={pagerConfig?.maxNumber || undefined}
              autoFocus
            />
            {pagerConfig?.minNumber != null && pagerConfig?.maxNumber != null && (
              <small style={{ color: '#aaa', display: 'block', marginTop: '6px' }}>
                {pagerConfig.minNumber} – {pagerConfig.maxNumber}
              </small>
            )}
          </div>
        )}

        {activeMethod === 'sms' && (
          <div className="mt-4">
            <AvForm>
              <div className="d-flex justify-content-center">
                <div style={{ maxWidth: '260px', width: '100%' }}>
                  <TelFormField
                    groupClassName="w-100 mb-0"
                    propertyName="smsPhoneNumber"
                    autocomplete="tel"
                    object={{ smsPhoneNumber: smsPhone }}
                    onChange={(val) => setSmsPhone(val)}
                  />
                </div>
              </div>
            </AvForm>
          </div>
        )}

        {activeMethod === 'email' && (
          <div className="mt-4">
            <AvForm>
              <div className="d-flex justify-content-center">
                <div style={{ maxWidth: '260px', width: '100%' }}>
                  <input
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    className="form-control"
                    placeholder={intl.formatMessage({ id: 'email' })}
                    value={emailAddress}
                    onChange={(e) => setEmailAddress(e.target.value)}
                  />
                </div>
              </div>
            </AvForm>
          </div>
        )}

        {activeMethod === 'whatsapp' && (
          <div className="text-center mt-4">
            {(currentOrder?.id || order?.id) ? (
              alreadyHasNotificationMethod ? (
                <p style={{ color: '#25D366', fontSize: '0.9rem', margin: 0, fontWeight: 600, fontFamily: 'Lexend, sans-serif' }}>
                  <FormattedMessage
                    id="whatsappEnabledConfirmation"
                    defaultMessage="WhatsApp notifications enabled. Tap Continue to place your order."
                  />
                </p>
              ) : (
              <>
                <p style={{ color: '#888', fontSize: '0.85rem', marginBottom: '12px', fontFamily: 'Lexend, sans-serif' }}>
                  <FormattedMessage
                    id="whatsappEnableHint"
                    defaultMessage="Tap below to enable WhatsApp notifications for your order"
                  />
                </p>
                <a
                  href={generateWhatsAppLink(config.whatsAppPhoneNumber, intl, currentOrder?.id || order?.id)}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={handleOpenWhatsApp}
                  className="btn btn-block"
                  style={{
                    background: '#25D366',
                    color: '#fff',
                    borderRadius: '12px',
                    padding: '12px',
                    fontWeight: 600,
                    fontFamily: 'Lexend, sans-serif',
                    fontSize: '0.9rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    maxWidth: '280px',
                    margin: '0 auto',
                    textDecoration: 'none',
                  }}
                >
                  <WhatsAppIcon size={20} color="#fff" />
                  <FormattedMessage id="openWhatsappLabel" defaultMessage="Open WhatsApp" />
                </a>
              </>
              )
            ) : (
              <p style={{ color: '#aaa', fontSize: '0.85rem', margin: 0, fontFamily: 'Lexend, sans-serif' }}>
                <FormattedMessage
                  id="whatsappPreCheckoutHint"
                  defaultMessage="WhatsApp notifications will be available after your order is placed."
                />
              </p>
            )}
          </div>
        )}
      </ModalBody>

      <ModalFooter className="border-0 px-4 pb-4 pt-0 flex-column">
        <Button
          block
          disabled={isConfirmDisabled() || isSubmitting}
          onClick={handleConfirm}
          style={{
            background: primaryColor,
            borderColor: primaryColor,
            borderRadius: '12px',
            padding: '13px',
            fontSize: '0.95rem',
            fontWeight: 600,
            fontFamily: 'Lexend, sans-serif',
            opacity: isConfirmDisabled() ? 0.5 : 1,
          }}
        >
          {isSubmitting ? '...' : (
            <FormattedMessage id="continueLabel" defaultMessage="Continue" />
          )}
        </Button>
        {!isMandatory && (
          <button
            className="btn btn-link"
            style={{ color: '#aaa', fontSize: '0.8rem', marginTop: '4px', textDecoration: 'none' }}
            onClick={handleSkip}
          >
            <FormattedMessage id="skipLabel" defaultMessage="Skip" />
          </button>
        )}
      </ModalFooter>
    </Modal>
  );
};

const mapStateToProps = (state) => ({
  isOpen: state.modals.isOrderNotificationsModalOpen,
  store: state.store.store,
  me: state.me.me,
  data: state.modals.orderInfoModalData,
  currentOrder: state.store.order,
  cart: state.store.cart,
  checkout: state.store.checkout,
  menuItems: state.store.items,
  menuItemDatas: state.store.itemDatas,
  kiosk: state.store.kiosk,
  orderNotificationsModalData: state.modals.orderNotificationsModalData,
  noSignupModal: state.modals.orderNotificationsModalData?.noSignupModal,
});

export default connect(mapStateToProps, {
  setIsOrderNotificationsModalOpen,
  setIsSignUpNoticeModalOpen,
  setIsCheckoutModalOpen,
  setCheckoutAction,
  fetchMeWithPromise,
  setOrderInfoModalData,
  setIsOrderInfoModalOpen,
  setCart,
  setOrder,
  setOrderHash,
})(OrderNotificationsModal);
